/**
 * Cel-shading + ink-outline system.
 *
 * The approved art direction is "thick black contour lines, chunky readable
 * silhouettes, banded flat colour". Three techniques combine to produce it:
 *
 *   1. SHADING — MeshToonMaterial with a hand-built N-step gradient ramp gives
 *      hard terminator bands instead of smooth falloff. We then patch the shader
 *      to (a) tint shadow bands toward the palette instead of muddy black, and
 *      (b) add a rim/fresnel wrap light so silhouettes stay legible against
 *      same-value backgrounds.
 *
 *   2. SILHOUETTE OUTLINES — inverted-hull. A duplicate of the mesh is rendered
 *      with `side: BackSide` and its vertices pushed outward. What remains
 *      visible is a shell peeking past the silhouette: a real ink contour with
 *      weight that follows form, unlike a uniform screen-space Sobel hairline.
 *
 *   3. INTERIOR LINES — see `postfx.ts`. Inverted hulls physically cannot draw
 *      the seam between a machine's front and side panel, because there is no
 *      silhouette there. A depth+normal edge pass draws those, thinner.
 *
 * ---------------------------------------------------------------------------
 * Why the hull shell used to only partially wrap silhouettes
 * ---------------------------------------------------------------------------
 *
 * Three separate bugs, all fixed here:
 *
 * a) **Welding by a quantised position key.** Bucketing `round(p / eps)` splits
 *    two vertices that sit either side of a bucket boundary — 0.00004999 and
 *    0.00005001 land in different buckets and never weld, so the shell tears at
 *    exactly the corners it most needs to be watertight. Fixed with union-find
 *    over the 27-cell neighbourhood plus a real distance test.
 *
 * b) **Extruding along the full 3D view normal.** `viewPos += viewNormal * t`
 *    pushes vertices whose normal faces the camera *toward* the camera. On any
 *    mesh with interior curvature, parts of the back shell then punch through
 *    the front of the fill and appear as black splotches in the middle of a
 *    flat face. Fixed by extruding only in the screen plane (`viewNormal.xy`)
 *    and biasing the shell *away* from the camera in view Z. A pleasant side
 *    effect: this also gives open, single-sided geometry (planes, ribbons) a
 *    real outline, which the 3D extrusion could never do.
 *
 * c) **Frustum culling against the un-inflated bounding sphere.** The shell is
 *    larger than the mesh, so at the screen edge three culls the object while
 *    its outline should still be visible, and the contour pops. Fixed by
 *    inflating the shared bounding sphere by the outline thickness.
 *
 * Plus a guard: on geometry thinner than twice the outline width the shell
 * swallows the fill entirely and the object renders as a black blob. Thickness
 * is clamped against the part's smallest bounding-box dimension — measured, as
 * of this revision, in SCREEN pixels and through the object's real world scale.
 * See `INK.cap` for why the fraction is 0.45 and not the original 0.25.
 */
import {
  BackSide,
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  Camera,
  Color,
  ConeGeometry,
  CylinderGeometry,
  DataTexture,
  Group,
  Mesh,
  MeshToonMaterial,
  NearestFilter,
  PlaneGeometry,
  RedFormat,
  ShaderMaterial,
  SphereGeometry,
  TorusKnotGeometry,
  type ColorRepresentation,
  type Material,
} from 'three';
import { srgb } from './palette';

/* ------------------------------------------------------------------ *
 * Render layers
 * ------------------------------------------------------------------ */

/**
 * Layer reserved for inverted-hull shells.
 *
 * The post-processing normal/depth pass must NOT see them: a shell shares its
 * geometry with the fill, so under an override material the two are coincident
 * and z-fight, corrupting the very normals the interior-line pass reads.
 * Putting shells on their own layer lets that pass exclude them with a single
 * bitmask, no traversal.
 *
 * `Selection` in postprocessing uses layer 12 for bloom; keep these distinct.
 * Ink occluders (character sprites written into the geo buffer as a skip
 * marker) live on layer 2 so the colour pass never sees them.
 */
export const OUTLINE_LAYER = 1;
export const INK_OCCLUDER_LAYER = 2;

let inkLayerEnabled = false;

/**
 * Opt a camera into rendering outline shells, and switch `inked()` over to the
 * dedicated layer.
 *
 * Until this is called, shells stay on the default layer so `cel.ts` remains
 * usable standalone (tests, tools, a second camera) without outlines silently
 * vanishing. The engine calls it once at boot, before any scene is built.
 */
export function enableInkLayer(camera: Camera): void {
  camera.layers.enable(OUTLINE_LAYER);
  inkLayerEnabled = true;
}

export function isInkLayerEnabled(): boolean {
  return inkLayerEnabled;
}

/* ------------------------------------------------------------------ *
 * Toon gradient ramps
 * ------------------------------------------------------------------ */

const rampCache = new Map<string, DataTexture>();

/**
 * Build an N-step luminance ramp for MeshToonMaterial.
 * `stops` are the normalized brightness of each band, dark -> light.
 */
export function toonRamp(stops: readonly number[]): DataTexture {
  const key = stops.join(',');
  const cached = rampCache.get(key);
  if (cached) return cached;

  const data = new Uint8Array(stops.length);
  for (let i = 0; i < stops.length; i++) {
    data[i] = Math.round(Math.max(0, Math.min(1, stops[i]!)) * 255);
  }
  const tex = new DataTexture(data, stops.length, 1, RedFormat);
  tex.minFilter = NearestFilter;
  tex.magFilter = NearestFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  rampCache.set(key, tex);
  return tex;
}

/** Three canonical ramps. Most props use TWO_TONE; hero objects use THREE_TONE. */
export const RAMP = {
  /** Hard two-band read — maximum illustration feel. */
  TWO_TONE: () => toonRamp([0.44, 1.0]),
  /** Adds a mid band so large forms don't flatten out. */
  THREE_TONE: () => toonRamp([0.38, 0.72, 1.0]),
  /** Soft-ish four band for skin/character shading. */
  FOUR_TONE: () => toonRamp([0.42, 0.66, 0.86, 1.0]),
} as const;

/* ------------------------------------------------------------------ *
 * Cel material
 * ------------------------------------------------------------------ */

export interface CelOptions {
  color: ColorRepresentation;
  /** Gradient ramp; defaults to TWO_TONE. */
  ramp?: DataTexture;
  /**
   * Colour the shadow bands are tinted toward. Real illustrators never shade
   * with black — they shade toward a cool complement. Defaults to a deep green
   * pulled from the brand palette.
   */
  shadowTint?: ColorRepresentation;
  /** 0..1 how strongly shadow bands adopt `shadowTint`. */
  shadowTintStrength?: number;
  /** Rim light colour; defaults to warm cream. */
  rimColor?: ColorRepresentation;
  /** 0..1 rim intensity. 0 disables. */
  rimStrength?: number;
  /** Fresnel exponent — higher = tighter rim. */
  rimPower?: number;
  transparent?: boolean;
  opacity?: number;
  emissive?: ColorRepresentation;
  emissiveIntensity?: number;
  /**
   * Clip-space depth added to faces that point up. Horizontal tops under a
   * dimetric camera land closer than a character standing just south of them;
   * this pushes those faces back so the figure wins, while vertical sides
   * (the ones that should hide him when he is behind a wall) stay put.
   */
  upwardDepthPush?: number;
}

/** A MeshToonMaterial patched with palette-tinted shadows and a rim light. */
export class CelMaterial extends MeshToonMaterial {
  readonly celUniforms: {
    uShadowTint: { value: Color };
    uShadowTintStrength: { value: number };
    uRimColor: { value: Color };
    uRimStrength: { value: number };
    uRimPower: { value: number };
  };

  constructor(opts: CelOptions) {
    super({
      color: opts.color,
      gradientMap: opts.ramp ?? RAMP.TWO_TONE(),
      transparent: opts.transparent ?? false,
      opacity: opts.opacity ?? 1,
      emissive: opts.emissive ?? 0x000000,
      emissiveIntensity: opts.emissiveIntensity ?? 1,
    });

    this.celUniforms = {
      uShadowTint: { value: new Color(opts.shadowTint ?? srgb(0x2a4636)) },
      uShadowTintStrength: { value: opts.shadowTintStrength ?? 0.45 },
      uRimColor: { value: new Color(opts.rimColor ?? srgb(0xfce9cc)) },
      uRimStrength: { value: opts.rimStrength ?? 0.35 },
      uRimPower: { value: opts.rimPower ?? 2.6 },
    };

    const upwardDepthPush = Math.max(0, opts.upwardDepthPush ?? 0);

    this.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, this.celUniforms);
      if (upwardDepthPush > 0) {
        shader.uniforms.uUpwardDepthPush = { value: upwardDepthPush };
      }

      shader.fragmentShader = shader.fragmentShader
        .replace(
          '#include <common>',
          `#include <common>
           uniform vec3  uShadowTint;
           uniform float uShadowTintStrength;
           uniform vec3  uRimColor;
           uniform float uRimStrength;
           uniform float uRimPower;
           ${upwardDepthPush > 0 ? 'uniform float uUpwardDepthPush;' : ''}`
        )
        // After lighting resolves, `outgoingLight` holds the shaded colour.
        // Pull the darkest areas toward the tint, then add the fresnel rim.
        .replace(
          '#include <output_fragment>',
          `
          {
            // Luminance of the resolved lighting tells us which band we landed in.
            float lum = dot( outgoingLight, vec3( 0.2126, 0.7152, 0.0722 ) );
            float shadowMask = 1.0 - smoothstep( 0.05, 0.55, lum );
            vec3 tinted = mix(
              outgoingLight,
              outgoingLight * uShadowTint * 1.9,
              shadowMask * uShadowTintStrength
            );

            // Fresnel rim in view space; geometryNormal/vViewPosition are in scope.
            vec3 V = normalize( vViewPosition );
            float fres = pow( 1.0 - clamp( dot( normalize( normal ), V ), 0.0, 1.0 ), uRimPower );
            tinted += uRimColor * fres * uRimStrength;

            outgoingLight = tinted;
          }
          ${
            upwardDepthPush > 0
              ? `gl_FragDepth = gl_FragCoord.z;
          {
            vec3 viewUp = viewMatrix[ 1 ].xyz;
            float up = dot( normalize( normal ), normalize( viewUp ) );
            if ( up > 0.45 ) {
              gl_FragDepth = min( gl_FragCoord.z + uUpwardDepthPush, 0.999 );
            }
          }`
              : ''
          }
          #include <output_fragment>`
        );
    };

    // Force a fresh program when celUniforms change identity.
    this.customProgramCacheKey = () =>
      upwardDepthPush > 0 ? `cel-v2-up${upwardDepthPush}` : 'cel-v2';
  }
}

/** Convenience factory. */
export function cel(opts: CelOptions): CelMaterial {
  return new CelMaterial(opts);
}

/* ------------------------------------------------------------------ *
 * Inverted-hull ink outline
 * ------------------------------------------------------------------ */

/** Minimal union-find over vertex indices. */
function findRoot(parent: Int32Array, i: number): number {
  let r = i;
  while (parent[r] !== r) r = parent[r]!;
  // Path compression, so the second pass is effectively O(1) per vertex.
  let c = i;
  while (parent[c] !== r) {
    const next = parent[c]!;
    parent[c] = r;
    c = next;
  }
  return r;
}

/**
 * Weld vertices by position, average their normals, and store the result as
 * `aOutlineNormal`. Without this, extruding a hard-edged mesh tears its shell
 * apart at every corner — the single most common inverted-hull artifact.
 *
 * Correctness detail: a plain hash of `round(p / eps)` is NOT a valid weld. Two
 * vertices `eps/100` apart still land in different cells whenever they straddle
 * a cell boundary, which on an axis-aligned box is *every single corner*. This
 * version hashes into cells of side `eps`, then unions each vertex with every
 * candidate in the 27 surrounding cells that is genuinely within `eps`.
 */
export function computeOutlineNormals(geometry: BufferGeometry, epsilon = 1e-4): BufferGeometry {
  if (geometry.getAttribute('aOutlineNormal')) return geometry;

  const pos = geometry.getAttribute('position');
  const nrm = geometry.getAttribute('normal');
  if (!pos || !nrm) return geometry;

  const count = pos.count;
  const eps2 = epsilon * epsilon;
  const inv = 1 / epsilon;

  const parent = new Int32Array(count);
  for (let i = 0; i < count; i++) parent[i] = i;

  // Spatial hash: cell key -> vertex indices in that cell.
  const cells = new Map<string, number[]>();
  const cx = new Int32Array(count);
  const cy = new Int32Array(count);
  const cz = new Int32Array(count);

  for (let i = 0; i < count; i++) {
    const gx = Math.floor(pos.getX(i) * inv);
    const gy = Math.floor(pos.getY(i) * inv);
    const gz = Math.floor(pos.getZ(i) * inv);
    cx[i] = gx;
    cy[i] = gy;
    cz[i] = gz;
    const key = gx + '|' + gy + '|' + gz;
    const list = cells.get(key);
    if (list) list.push(i);
    else cells.set(key, [i]);
  }

  for (let i = 0; i < count; i++) {
    const px = pos.getX(i);
    const py = pos.getY(i);
    const pz = pos.getZ(i);
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (let dz = -1; dz <= 1; dz++) {
          const list = cells.get(cx[i]! + dx + '|' + (cy[i]! + dy) + '|' + (cz[i]! + dz));
          if (!list) continue;
          for (const j of list) {
            if (j <= i) continue;
            const ex = pos.getX(j) - px;
            const ey = pos.getY(j) - py;
            const ez = pos.getZ(j) - pz;
            if (ex * ex + ey * ey + ez * ez > eps2) continue;
            const ri = findRoot(parent, i);
            const rj = findRoot(parent, j);
            if (ri !== rj) parent[rj] = ri;
          }
        }
      }
    }
  }

  // Accumulate normals per weld group, then write the normalised average back
  // to every member. Accumulators are flat arrays — no per-vertex allocation.
  const accX = new Float64Array(count);
  const accY = new Float64Array(count);
  const accZ = new Float64Array(count);

  for (let i = 0; i < count; i++) {
    const r = findRoot(parent, i);
    accX[r] = accX[r]! + nrm.getX(i);
    accY[r] = accY[r]! + nrm.getY(i);
    accZ[r] = accZ[r]! + nrm.getZ(i);
  }

  const out = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const r = findRoot(parent, i);
    let ax = accX[r]!;
    let ay = accY[r]!;
    let az = accZ[r]!;
    const len = Math.sqrt(ax * ax + ay * ay + az * az);
    if (len < 1e-9) {
      // Fully opposed normals (a zero-thickness plane, a double-sided card):
      // there is no meaningful average, so keep the per-vertex normal.
      ax = nrm.getX(i);
      ay = nrm.getY(i);
      az = nrm.getZ(i);
    } else {
      ax /= len;
      ay /= len;
      az /= len;
    }
    out[i * 3] = ax;
    out[i * 3 + 1] = ay;
    out[i * 3 + 2] = az;
  }

  geometry.setAttribute('aOutlineNormal', new BufferAttribute(out, 3));
  return geometry;
}

export interface OutlineOptions {
  /** Thickness in WORLD units (constant screen width under an ortho camera). */
  thickness?: number;
  color?: ColorRepresentation;
  /**
   * When true, thickness scales with distance so outlines keep a constant pixel
   * width under a PERSPECTIVE camera. Leave false for orthographic scenes.
   */
  perspectiveCompensate?: boolean;
  /**
   * How far the shell is pulled TOWARD the camera, as a multiple of thickness.
   *
   * This is what makes contact outlines survive. Widening the shell in the
   * screen plane moves its vertices *down-screen* at the bottom of a prop, and
   * down-screen on a ground plane means nearer to the camera — by roughly 0.9
   * world units per unit of screen offset at our 42-degree tilt. So the floor
   * wins the depth test and the entire bottom half of every silhouette silently
   * loses its ink while the top half keeps it. That lopsided contour is exactly
   * the "outlines only partially wrap" symptom. Pulling the shell forward by a
   * little more than the widening distance fixes it.
   *
   * It cannot punch through the fill: a closed mesh's back faces sit a whole
   * object-thickness behind its front faces, and `thickness` is separately
   * clamped to a quarter of the smallest bounding-box dimension.
   */
  depthBias?: number;
  /**
   * Set false to use the legacy full-3D extrusion. Screen-plane extrusion is
   * strictly better for closed meshes and is the only mode that outlines open
   * geometry at all, so this defaults to true.
   */
  screenSpace?: boolean;
  /**
   * Smallest bounding-box dimension of the geometry, in local units. Feeds the
   * shader's anti-swallow clamp. 0 or omitted disables that clamp.
   */
  geoMinDim?: number;
}

/* ------------------------------------------------------------------ *
 * Screen-space ink scale
 *
 * Authored thickness is a WORLD quantity, which means its on-screen weight is
 * entirely at the mercy of the camera's zoom. Framing a whole 4x4 house made
 * every contour a 1-2px hairline, and the "thick black contour" that defines
 * the approved look simply stopped existing — measured, our playfield carried
 * 4.9% near-black pixels against the reference's 15.9%.
 *
 * The fix is to treat authored thickness as *relative weight* and resolve it
 * against a live world-units-per-pixel figure, then clamp the result in PIXELS:
 * never thinner than `minPx`, and never more than a fraction of the part's own
 * on-screen size (so a thin limb still cannot be swallowed into a black blob).
 *
 * Silhouette is only half of the ink budget. The pen lines on seams, drawers,
 * hems and panel edges come from `postfx.ts`'s depth+normal pass, and they are
 * deliberately about a third of this weight: silhouette is a brush, interior is
 * a pen. Retune the two together or the drawing stops reading as one hand.
 *
 * These uniform objects are shared by reference across every ink material, so
 * the engine retunes the whole scene's linework with a single assignment.
 * ------------------------------------------------------------------ */

export const INK = {
  /** World units per screen pixel. Engine updates this on resize/zoom. */
  scale: { value: 0.0045 },
  /**
   * Hard floor on contour weight, in CSS pixels.
   *
   * ── Lowered 6.2 -> 4.4 on 2026-08-06, on a USER report ────────────────────
   *
   * The user's words were "the black line seems too thick". Measured, at
   * 1600x900 / high tier, as a run-length distribution over near-black pixels
   * (`tools/linewidth.mjs`):
   *
   *   approved reference game1-desktop, normalised to 1600px   median 3.5-4 px
   *   supplied furniture art (kitchen-fridge.png)              median   4 px
   *   our own player sprite, as composited in frame            median   3 px
   *   ours, before this change                                 median   5-8 px
   *
   * The earlier 6.2 was read off the reference's *p75*, not its median, so it
   * pinned our median where the reference's heaviest quartile sits.
   *
   * The second, less obvious half of the same defect was the user's word
   * "irregular". A shell's width is
   *
   *     min( max( thickness/scale * gain, minPx ), onScreenExtent * cap )
   *
   * and at minPx 6.2 the frame split into two populations: thin floor
   * coverings pinned by the CAP at 3.87-4.15 px, and everything bulkier pinned
   * by the FLOOR at 6.2 px (census in `tools/hulldump.mjs`). Neighbouring props
   * therefore carried contours 1.6x apart in weight, which reads as a drawing
   * error rather than as style. At 4.4 the whole frame lands in a 3.87-4.42 px
   * band — 1.14x — so the world is drawn by one hand, and by the same hand that
   * drew the sprites it now has to sit beside.
   *
   * Note the two clamps swap roles at the whole-house overview, where
   * `worldPerPixel` is ~4x larger: there `minPx` binds on everything and IS the
   * contour, which is why it must not be dropped much below 4.
   */
  minPx: { value: 4.4 },
  /** Global multiplier on authored weight, for art-direction tuning. */
  gain: { value: 1.0 },
  /**
   * Anti-swallow ceiling: ink may never exceed this fraction of the part's own
   * smallest ON-SCREEN extent, so a thin limb keeps some fill instead of
   * collapsing into a black blob.
   *
   * Raised from 0.30 because 0.30 was the real cause of the patchwork contour.
   * At 0.30 the character's 14.6px-wide forearm capped at 4.4px while its 66px
   * head kept the full weight — a visible step in line weight across one figure,
   * which reads as a drawing error rather than as style. The shell is extruded
   * in the SCREEN PLANE, so the two rims of a thin part move apart rather than
   * toward each other; the actual failure mode is the far-side shell being
   * pulled through the fill by `depthBias`, which only bites past roughly
   * 0.7x the on-screen extent. 0.40 sits comfortably inside that and still
   * leaves a face's small features — eyes, brows — reading as marks rather than
   * as one solid mass.
   *
   * KEPT at 0.40 when `minPx` came down to 4.4. The 0.30 -> 0.40 change was
   * made to close a step in weight across one figure; lowering `minPx` closes
   * that step from the other side (a 14.6px forearm caps at 5.84px, above the
   * 4.4px floor, so it is no longer clamped at all). Dropping the cap as well
   * would re-open the step in the opposite direction.
   */
  cap: { value: 0.4 },
  /**
   * The cap's own floor, as a fraction of `minPx`.
   *
   * Below this the cap stops shrinking and the part is simply allowed to become
   * solid ink. That is not a failure: an illustrator draws a 2px-wide wire as a
   * line, not as a line plus a sliver of fill. Without the floor, ink on small
   * props evaporates the moment the camera pulls back to the whole-house
   * overview, which is exactly the zoom-dependent hairline we are fixing.
   */
  capFloor: { value: 0.3 },
};

/** Called by the engine whenever the ortho frustum or viewport changes. */
export function setInkScale(worldPerPixel: number): void {
  if (Number.isFinite(worldPerPixel) && worldPerPixel > 0) {
    INK.scale.value = worldPerPixel;
  }
}

/** Art-direction knobs, also reachable from the console for live tuning. */
export function setInkWeight(minPx: number, gain = INK.gain.value, cap = INK.cap.value): void {
  INK.minPx.value = Math.max(0, minPx);
  INK.gain.value = Math.max(0, gain);
  INK.cap.value = Math.max(0.05, cap);
}

/** Ink-shell material. Renders backfaces only, pushed out along smooth normals. */
export function outlineMaterial(opts: OutlineOptions = {}): ShaderMaterial {
  const thickness = opts.thickness ?? 0.035;
  const color = new Color(opts.color ?? 0x111111);
  const compensate = opts.perspectiveCompensate ?? false;
  const screenSpace = opts.screenSpace ?? true;
  const depthBias = opts.depthBias ?? 0.55;

  return new ShaderMaterial({
    name: 'InkOutline',
    side: BackSide,
    // Test against the fill so the stroke sits on the silhouette; do not
    // write depth. A toward-camera bias used to keep the stroke above the
    // floor, and that same closer depth let the line win against a character
    // standing next to the wall. The floor no longer writes depth, so the
    // stroke still survives, and the player (drawn later) can overwrite it.
    depthTest: true,
    depthWrite: false,
    uniforms: {
      uThickness: { value: thickness },
      uDepthBias: { value: depthBias },
      uColor: { value: color },
      // Smallest bounding-box dimension in geometry units; 0 disables the
      // anti-swallow clamp. Set by inked()/addInkOutline().
      uGeoMinDim: { value: opts.geoMinDim ?? 0 },
      // Shared by reference — do NOT clone these.
      uInkScale: INK.scale,
      uMinInkPx: INK.minPx,
      uInkGain: INK.gain,
      uInkCap: INK.cap,
      uInkCapFloor: INK.capFloor,
    },
    vertexShader: /* glsl */ `
      attribute vec3 aOutlineNormal;
      uniform float uThickness;
      uniform float uDepthBias;
      uniform float uGeoMinDim;
      uniform float uInkScale;
      uniform float uMinInkPx;
      uniform float uInkGain;
      uniform float uInkCap;
      uniform float uInkCapFloor;

      void main() {
        // Prefer the welded normal; fall back to the shading normal if absent.
        // A missing attribute reads as (0,0,0), which the length test catches.
        vec3 n = length( aOutlineNormal ) > 0.0001 ? aOutlineNormal : normal;

        // Work in view space so non-uniform object scale can't distort the
        // outline weight — a common cause of "one side thicker than the other".
        vec4 viewPos    = modelViewMatrix * vec4( position, 1.0 );
        vec3 viewNormal = normalize( normalMatrix * n );

        // Resolve authored world weight into PIXELS at the current zoom, then
        // clamp in pixel space. This is what keeps contours readable whether we
        // are framing one cell or the whole house.
        float invScale = 1.0 / max( uInkScale, 1e-6 );
        float wPx = uThickness * invScale * uInkGain;
        wPx = max( wPx, uMinInkPx );

        // Anti-swallow: never widen past a fraction of the part's own on-screen
        // smallest extent, or a thin limb becomes a solid black blob.
        //
        // uGeoMinDim is in GEOMETRY units, which are only world units when the
        // object sits at scale 1. Reading it as though it were world units makes
        // the clamp too tight on anything scaled up and too loose on anything
        // scaled down — the second-order cause of uneven contour weight, after
        // the fraction itself. The model-view basis lengths give the true world
        // scale (the view matrix is rigid, so it contributes none of its own);
        // the smallest of the three is the conservative reading.
        if ( uGeoMinDim > 0.0 ) {
          vec3 basis = vec3(
            length( modelViewMatrix[ 0 ].xyz ),
            length( modelViewMatrix[ 1 ].xyz ),
            length( modelViewMatrix[ 2 ].xyz )
          );
          float objScale = min( basis.x, min( basis.y, basis.z ) );
          float onScreenPx = uGeoMinDim * objScale * invScale;
          // Floor the ceiling: past a point a part is simply too small to hold
          // both an outline and a fill, and a solid ink mark is the correct
          // drawing. Letting the cap fall to zero is what made small props lose
          // their line entirely at the whole-house overview zoom.
          wPx = min( wPx, max( onScreenPx * uInkCap, uMinInkPx * uInkCapFloor ) );
        }

        float w = wPx * uInkScale;
        ${
          compensate
            ? '// Keep constant pixel width under perspective.\n        w *= -viewPos.z * 0.35;'
            : '// Orthographic: pixel-resolved width is already screen-constant.'
        }

        ${
          screenSpace
            ? `// Widen ONLY in the screen plane. At a silhouette the view normal
        // is already perpendicular to the eye, so this is identical to the 3D
        // extrusion where it matters — but interior vertices no longer travel
        // toward the camera and punch through the fill.
        vec2 dir = viewNormal.xy;
        float len = length( dir );
        dir = len > 1e-5 ? dir / len : vec2( 0.0 );
        viewPos.xy += dir * w;
        // View space looks down -Z, so ADDING pulls the shell toward the camera.
        // Only the widened rim needs it, so scale by how far this vertex
        // actually moved: interior vertices stay exactly where they were.
        viewPos.z  += w * uDepthBias * len;`
            : `viewPos.xyz += viewNormal * w;`
        }

        gl_Position = projectionMatrix * viewPos;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 uColor;
      void main() { gl_FragColor = vec4( uColor, 1.0 ); }
    `,
  });
}

/**
 * Largest safe outline thickness for a geometry.
 *
 * On a shape thinner than 2x the outline width, the back shell pushes clean
 * through the front face and the object renders as a solid black blob. Cap the
 * request at a quarter of the smallest bounding-box dimension.
 */
export function maxSafeThickness(geometry: BufferGeometry): number {
  // Cache: `inflateBounds` grows the bounding box afterwards, so recomputing on
  // a later `inked()` call with the same shared geometry would read an already
  // padded box and creep the cap upwards.
  const cached = geometry.userData.inkMaxThickness as number | undefined;
  if (typeof cached === 'number') return cached;

  if (!geometry.boundingBox) geometry.computeBoundingBox();
  const bb = geometry.boundingBox;
  if (!bb) return Infinity;
  const sx = bb.max.x - bb.min.x;
  const sy = bb.max.y - bb.min.y;
  const sz = bb.max.z - bb.min.z;
  // Ignore genuinely flat axes: a plane is 0 thick and still wants an outline,
  // and the screen-space extrusion handles it correctly.
  const dims = [sx, sy, sz].filter((d) => d > 1e-4);
  const limit = dims.length === 0 ? Infinity : Math.min(...dims) * 0.25;
  geometry.userData.inkMaxThickness = limit;
  return limit;
}

/**
 * Smallest non-degenerate bounding-box dimension, in geometry units.
 * Reuses `maxSafeThickness`'s cache (which stores that dimension x 0.25).
 * Returns 0 for flat/degenerate geometry, which disables the anti-swallow clamp.
 */
export function geometryMinDim(geometry: BufferGeometry): number {
  const q = maxSafeThickness(geometry);
  return Number.isFinite(q) ? q * 4 : 0;
}

/**
 * Grow the shared bounding sphere so the frustum culler accounts for the shell.
 * Without this the contour pops off at the edge of the screen a frame early.
 */
function inflateBounds(geometry: BufferGeometry, amount: number): void {
  // Idempotent: `inked()` is routinely called several times with one shared
  // geometry, and inflating on every call would grow the sphere without bound.
  if (geometry.userData.inkBoundsInflated) return;
  geometry.userData.inkBoundsInflated = true;

  if (!geometry.boundingSphere) geometry.computeBoundingSphere();
  if (geometry.boundingSphere) geometry.boundingSphere.radius += amount;
  if (!geometry.boundingBox) geometry.computeBoundingBox();
  if (geometry.boundingBox) geometry.boundingBox.expandByScalar(amount);
}

/**
 * Attach an ink shell to an existing mesh.
 *
 * Returned as a sibling-style child of the mesh so it inherits every transform
 * automatically, which is what scenes want when they animate a prop.
 */
export function addInkOutline(mesh: Mesh, outlineOpts: OutlineOptions = {}): Mesh {
  const geometry = mesh.geometry;
  computeOutlineNormals(geometry);

  const thickness = outlineOpts.thickness ?? 0.035;
  // The shader now clamps ink in SCREEN space, so we deliberately no longer
  // strangle the authored weight against world-space geometry size here. That
  // world clamp is exactly what reduced the character's 0.07-unit limbs to a
  // 0.017-unit hairline while the head kept 0.026 — a patchwork contour.
  // A fixed generous inflation keeps frustum culling safe at any zoom.
  const geoMinDim = geometryMinDim(geometry);
  inflateBounds(geometry, 0.35);

  const shell = new Mesh(geometry, outlineMaterial({ ...outlineOpts, thickness, geoMinDim }));
  shell.castShadow = false;
  shell.receiveShadow = false;
  shell.renderOrder = mesh.renderOrder - 1;
  shell.raycast = () => {};
  shell.userData.isOutline = true;
  if (inkLayerEnabled) shell.layers.set(OUTLINE_LAYER);
  // Local identity: the shell rides the mesh's own transform.
  mesh.add(shell);
  mesh.userData.shell = shell;
  return shell;
}

/**
 * Build a cel-shaded, ink-outlined object in one call.
 *
 * Returns a Group holding the shaded mesh plus its outline shell, so callers can
 * transform them together. The shell is `renderOrder`-biased behind the fill.
 */
export function inked(
  geometry: BufferGeometry,
  celOpts: CelOptions,
  outlineOpts: OutlineOptions = {}
): Group {
  computeOutlineNormals(geometry);

  const thickness = outlineOpts.thickness ?? 0.035;
  // The shader now clamps ink in SCREEN space, so we deliberately no longer
  // strangle the authored weight against world-space geometry size here. That
  // world clamp is exactly what reduced the character's 0.07-unit limbs to a
  // 0.017-unit hairline while the head kept 0.026 — a patchwork contour.
  // A fixed generous inflation keeps frustum culling safe at any zoom.
  const geoMinDim = geometryMinDim(geometry);
  inflateBounds(geometry, 0.35);

  const group = new Group();

  const fill = new Mesh(geometry, cel(celOpts));
  fill.castShadow = true;
  fill.receiveShadow = true;
  fill.renderOrder = 1;

  const shell = new Mesh(geometry, outlineMaterial({ ...outlineOpts, thickness, geoMinDim }));
  shell.castShadow = false;
  shell.receiveShadow = false;
  shell.renderOrder = 0;
  // The shell must never contribute to raycasts/interaction picking.
  shell.raycast = () => {};
  shell.userData.isOutline = true;
  // Hidden from the post-processing normal/depth pass; see OUTLINE_LAYER.
  if (inkLayerEnabled) shell.layers.set(OUTLINE_LAYER);

  group.add(shell, fill);
  group.userData.fill = fill;
  group.userData.shell = shell;
  return group;
}

/** Retrieve the shaded mesh from a group built by `inked`. */
export function fillOf(group: Group): Mesh | undefined {
  return group.userData.fill as Mesh | undefined;
}

/** Retrieve the ink shell from a group built by `inked`. */
export function shellOf(group: Group): Mesh | undefined {
  return group.userData.shell as Mesh | undefined;
}

/** Retune every outline shell under a node — useful for zoom-dependent weight. */
export function setOutlineThickness(root: Group | Mesh, thickness: number): void {
  root.traverse((o) => {
    if (!o.userData.isOutline) return;
    const mat = (o as Mesh).material as unknown as ShaderMaterial | undefined;
    const u = mat?.uniforms?.['uThickness'];
    if (u) u.value = thickness;
  });
}

/* ------------------------------------------------------------------ *
 * Verification rig
 * ------------------------------------------------------------------ */

/**
 * A row of deliberately awkward shapes for eyeballing outline robustness:
 * hard 90-degree corners, a mixed hard/smooth edge, a sharp apex, a wedge with
 * an acute dihedral, a very thin slab (the thickness-clamp case) and an open
 * single-sided plane (the case the old 3D extrusion could not outline at all).
 *
 * Wired into the engine behind `?debug=outline`.
 */
export function makeOutlineTestRig(thickness = 0.045): Group {
  const root = new Group();
  const shapes: Array<[BufferGeometry, number]> = [
    [new BoxGeometry(1.6, 1.6, 1.6), 0xdf1725],
    [new CylinderGeometry(0.85, 0.85, 1.7, 16), 0x74ac4b],
    [new ConeGeometry(0.95, 1.9, 5), 0xf8c226],
    [new TorusKnotGeometry(0.6, 0.22, 96, 12), 0x3c5142],
    [new BoxGeometry(1.8, 0.06, 1.8), 0xa4cda8],
    [new SphereGeometry(0.9, 24, 16), 0xfce9cc],
    [new PlaneGeometry(1.6, 1.6), 0xddf2e0],
  ];

  shapes.forEach(([geo, color], i) => {
    const g = inked(geo, { color, ramp: RAMP.THREE_TONE() }, { thickness });
    g.position.set((i - (shapes.length - 1) / 2) * 2.4, 1.1, 0);
    g.rotation.y = 0.35;
    root.add(g);
  });

  root.userData.isOutlineTestRig = true;
  return root;
}

/* ------------------------------------------------------------------ *
 * Teardown
 * ------------------------------------------------------------------ */

/** Dispose every material, texture and geometry under a node. */
export function disposeTree(root: { traverse: (cb: (o: any) => void) => void }): void {
  const seenGeo = new Set<BufferGeometry>();
  const seenMat = new Set<Material>();
  root.traverse((o: any) => {
    if (o.geometry && !seenGeo.has(o.geometry)) {
      seenGeo.add(o.geometry);
      o.geometry.dispose();
    }
    const m = o.material;
    if (!m) return;
    for (const mat of Array.isArray(m) ? m : [m]) {
      if (!mat || seenMat.has(mat)) continue;
      seenMat.add(mat);
      // Baked canvas textures are the norm in this project, so a material is
      // usually the only owner of its maps. Toon ramps are module-cached and
      // deliberately skipped (`gradientMap` is absent from this list); anything
      // else deliberately shared across scenes can opt out with
      // `texture.userData.shared = true`.
      for (const key of ['map', 'emissiveMap', 'alphaMap', 'aoMap', 'bumpMap', 'normalMap']) {
        const tex = (mat as unknown as Record<string, unknown>)[key] as
          | { dispose?: () => void; userData?: Record<string, unknown> }
          | undefined
          | null;
        if (tex && tex.userData?.['shared'] !== true && typeof tex.dispose === 'function') {
          tex.dispose();
        }
      }
      mat.dispose();
    }
  });
}
